import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	CanWithdrawResponse,
	CaseModel,
	DataGatheringId,
	DataGatheringRequiredResponse,
	DraftModel,
	NotYetLoadedSliceStatuses,
	SliceStatus,
} from 'type-declarations';
import { fetchTransaction } from './fetch-thunks';
import {
	submitCase,
	submitDraft,
	submitSdgData,
	submitSdgIncludeScreen,
	validateAndSubmitSdg,
} from './submit-thunks';
import { FetchTransactionThunkResponse } from './models';
import { getCaseRoute, getDraftRoute } from './pathing';
import { validateSdgData } from './validate-thunks';
import * as Sentry from '@sentry/react';

export interface TransactionState {
	case: CaseModel | null;
	draft: DraftModel | null;
	dataGatheringDetails: DataGatheringRequiredResponse[];
	canWithdrawResponse: CanWithdrawResponse | null;
	fetchTransactionStatus: SliceStatus;
	errorMessage: string | null;
	pollUntilCaseFound: boolean;
	pollUntilPathChanges: boolean;
	path: string | null;
}

const initialState: TransactionState = {
	case: null,
	draft: null,
	dataGatheringDetails: [],
	canWithdrawResponse: null,
	fetchTransactionStatus: SliceStatus.IDLE,
	errorMessage: null,
	pollUntilCaseFound: false,
	pollUntilPathChanges: false,
	path: null,
};

export const transactionSlice = createSlice({
	name: 'transaction',
	initialState,
	reducers: {
		resetTransactionSlice: (state) => {
			Object.assign(state, initialState);
		},
		updatePollUntilPathChanges: (state, action) => {
			state.pollUntilPathChanges = action.payload;
		},
		updatePathString: (state, action) => {
			state.path = action.payload;
		},
	},
	extraReducers: (builder) => {
		//fetchTransaction
		builder.addCase(fetchTransaction.pending, (state) => {
			if (NotYetLoadedSliceStatuses.includes(state.fetchTransactionStatus)) {
				state.fetchTransactionStatus = SliceStatus.PENDING;
			} else state.fetchTransactionStatus = SliceStatus.REFRESHING;
		});
		builder.addCase(
			fetchTransaction.fulfilled,
			(state, action: PayloadAction<FetchTransactionThunkResponse>) => {
				state.fetchTransactionStatus = SliceStatus.SUCCEEDED;
				state.draft = action.payload.draft;
				state.case = action.payload.case;
				state.dataGatheringDetails = action.payload.dataGatheringDetails;
				//BACKEND JUNK - Filter out the default "None" screen returned
				//when there are no SDG requirements for the case. An empty array is fine.
				state.dataGatheringDetails = state.dataGatheringDetails.filter(
					(d) => d.dataGatheringScreen !== DataGatheringId.None
				);
				Sentry.setTag('partnerCaseID', state.draft?.partnerCaseId ?? '');
				state.canWithdrawResponse = action.payload.canWithdrawResponse;
				const isDraftRoute =
					!state.case ||
					(state.case && state.case.statusId === 'INCOMPLETE' && state.draft!.errors.length > 0);
				const newPath = isDraftRoute
					? getDraftRoute(state.draft!)
					: getCaseRoute(state.case!, state.dataGatheringDetails);

				if (state.pollUntilPathChanges) {
					state.pollUntilPathChanges = newPath === state.path;
				}
				state.path = newPath;
				if (state.case) {
					state.pollUntilCaseFound = false;
				}
			}
		);
		builder.addCase(fetchTransaction.rejected, (state) => {
			state.fetchTransactionStatus = SliceStatus.REJECTED;
		});
		//submitDraft
		builder.addCase(submitDraft.pending, (state) => {
			state.pollUntilPathChanges = true;
		});
		builder.addCase(submitDraft.fulfilled, (state) => {
			state.pollUntilPathChanges = true;
		});
		//submitCase
		builder.addCase(submitCase.pending, (state) => {
			state.pollUntilPathChanges = true;
		});
		builder.addCase(submitCase.fulfilled, (state) => {
			state.pollUntilPathChanges = true;
		});
		//submit SDG
		builder.addCase(submitSdgData.pending, (state) => {
			state.pollUntilPathChanges = true;
		});
		builder.addCase(submitSdgData.fulfilled, (state) => {
			state.pollUntilPathChanges = true;
		});
		//validate and submit SDG
		builder.addCase(validateAndSubmitSdg.pending, (state) => {
			state.pollUntilPathChanges = true;
		});
		builder.addCase(validateAndSubmitSdg.fulfilled, (state) => {
			state.pollUntilPathChanges = true;
		});
		//validate sdg data
		builder.addCase(validateSdgData.pending, (state) => {
			state.pollUntilPathChanges = true;
		});
		builder.addCase(validateSdgData.fulfilled, (state) => {
			state.pollUntilPathChanges = true;
		});
		//submit sdg and include screen
		//example: noa and csr
		builder.addCase(submitSdgIncludeScreen.pending, (state) => {
			state.pollUntilPathChanges = true;
		});
		builder.addCase(submitSdgIncludeScreen.fulfilled, (state) => {
			state.pollUntilPathChanges = true;
		});
	},
});

export const transactionReducer = transactionSlice.reducer;
export const transactionActions = transactionSlice.actions;
export const { resetTransactionSlice, updatePollUntilPathChanges, updatePathString } =
	transactionActions;
